import { useEffect, useCallback, useMemo } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';

import pollQuery from './pollAssetRegistrationStatus.graphql';
import { useAddAssetContext } from '../context/AddAssetContext';
import type { RegistrationStatusWithVin } from '../types';

const POLLING_INTERVAL = 3000;

export const usePollAssetRegistrationStatus = () => {
  const apolloClient = useApolloClient();

  const { setSavedState } = useAddAssetContext();

  const [fetchRegistration, { data, loading, error, called, stopPolling }] =
    useLazyQuery<
      AssetGroupingAssetRegistrationPollQuery,
      AssetGroupingAssetRegistrationPollQueryVariables
    >(pollQuery, {
      fetchPolicy: 'network-only',
      pollInterval: POLLING_INTERVAL,
      notifyOnNetworkStatusChange: true,
    });

  const startPolling = useCallback(
    (assetRegistrationId: string) => {
      fetchRegistration({ variables: { assetRegistrationId } });
    },
    [fetchRegistration],
  );

  const status = data?.managedAssetGroupingAssetRegistration?.status;

  const pollingFinished =
    called &&
    !loading &&
    (status === 'completed' || status === 'failed' || !!error);

  const assetRegistrationStatuses = useMemo(
    () =>
      data?.managedAssetGroupingAssetRegistration?.assetRegistrationStatuses ??
      [],
    [data?.managedAssetGroupingAssetRegistration?.assetRegistrationStatuses],
  );

  const reportUrl =
    data?.managedAssetGroupingAssetRegistration?.reportUrl ?? undefined;

  useEffect(() => {
    if (pollingFinished) {
      stopPolling();
      const statusesWithVin = assetRegistrationStatuses.filter(
        (s): s is RegistrationStatusWithVin => !!s.vin,
      );
      const { assetsAdded, assetsFailed } = statusesWithVin.reduce<{
        assetsAdded: RegistrationStatusWithVin[];
        assetsFailed: RegistrationStatusWithVin[];
      }>(
        (prev, registrationStatus) => {
          if (registrationStatus.status === 'completed') {
            return {
              ...prev,
              assetsAdded: [...prev.assetsAdded, registrationStatus],
            };
          }

          return {
            ...prev,
            assetsFailed: [...prev.assetsFailed, registrationStatus],
          };
        },
        { assetsAdded: [], assetsFailed: [] },
      );

      setSavedState((prev) => ({
        ...prev,
        registration: {
          ...prev.registration,
          assetsAdded,
          assetsFailed,
          reportUrl,
        },
      }));

      if (assetsAdded.length > 0) {
        apolloClient.refetchQueries({ include: ['DepotAssets'] });
      }
    }
  }, [
    pollingFinished,
    stopPolling,
    apolloClient,
    setSavedState,
    assetRegistrationStatuses,
    reportUrl,
  ]);

  return {
    startPolling,
    data,
    loading,
    error,
    pollingFinished,
  };
};
